"use client";

/**
 * View-tracking helpers for the marketing funnel (spec §4).
 *
 *   <TrackView>        — fires once on mount (page-level "…_viewed").
 *   <TrackSectionView> — fires once when the sentinel scrolls into view
 *                        (section-level "…_section_viewed"), via
 *                        IntersectionObserver, then disconnects.
 *
 * Both are no-ops when PostHog isn't initialized and never throw. They render
 * nothing visible (the section sentinel is a zero-height, aria-hidden div).
 */

import * as React from "react";
import posthog from "posthog-js";
import type { AnalyticsEvent } from "@/lib/analytics/events";

function safeCapture(event: AnalyticsEvent) {
  try {
    posthog.capture(event);
  } catch {
    /* analytics must never break the page */
  }
}

export function TrackView({ event }: { event: AnalyticsEvent }) {
  React.useEffect(() => {
    safeCapture(event);
    // Fire exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export function TrackSectionView({ event }: { event: AnalyticsEvent }) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const fired = React.useRef(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (!fired.current && entries.some((e) => e.isIntersecting)) {
          fired.current = true;
          safeCapture(event);
          obs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [event]);

  return <div ref={ref} aria-hidden style={{ height: 0 }} />;
}
